import React, { useEffect, useRef, useState } from 'react';
import { Box, Drawer, Grid, IconButton, Stack, Card, Typography, CircularProgress, } from "@mui/material"
import { useDispatch, useSelector } from "react-redux";
// icons
import CloseIcon from '@mui/icons-material/Close';
// utils and helpers
import { formatTimestampToWithoutTime } from "utilities/common";
import { getDiscrepancyApprovalDetails, scanDiscrepancyProduct, updateDiscrepancyApprovalStatus } from "../../../redux/slices/hyperwolf/inventoryDistribution";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../../redux/slices/modalSlice';
// custom components
import MDButton from 'components/MDButton';
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
import BarcodeScanArea from "components/BarcodeScanArea";
import { UploadPhoto } from "common/uploadPhoto";
import StatusText from "common/component/StatusText";

export default function DiscrepancyDetailsMissingSKU({ open, TransitionComponent, handleClose, setRefreshTable }) {
  const ref = useRef();
  const dispatch = useDispatch()

  const { user } = useSelector(state => state.auth || {})
  const [additionalNotes, setAdditionalNotes] = useState({});
  const [details, setDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setAdditionalNotes(prev => ({ ...prev, [name]: value }));
  };

  const handleDiscrepancyDetails = async () => {
    try {
      const response = await dispatch(getDiscrepancyApprovalDetails(open?.id)).unwrap()
      if (response?.data) {
        setDetails(response?.data || {})
      }
    } catch (error) {
      showToastMsg(error)
    }
  }

  const handleDeletePhoto = (image) => {
    setAdditionalNotes(prev => ({ ...prev, photos: prev.photos.filter((img) => img !== image) }))
  }

  const handleUpdateDiscrepancy = async (action) => {
    if (isLoading || !action) return;
    setIsLoading(true);

    try {
      dispatch(setModalLoader(true))
      const request = { id: open?.id, action, updatedBy: user?.name, ...additionalNotes }
      const response = await dispatch(updateDiscrepancyApprovalStatus(request)).unwrap()
      const { success, message, data } = response || {}

      if (data || success) {
        showToastMsg(message, "success")
        handleClose()
        setRefreshTable(prev => !prev);
        setAdditionalNotes({})
      }
    } catch (error) {
      showToastMsg(error)
    } finally {
      setIsLoading(false)
      dispatch(setModalLoader(false))
      dispatch(closeCustomModalDialog())
    }
  }

  const handleScanProduct = async (value) => {
    try {
      if (value) {
        const payload = { discrepancyId: open.id, productBatchId: value }
        const response = await dispatch(scanDiscrepancyProduct(payload)).unwrap()
        if (response?.success) {
          handleDiscrepancyDetails();
          setRefreshTable(prev => !prev);
          showToastMsg(response?.message || "Success", "success");
        }
      }
    }
    catch (error) {
      showToastMsg(error, "error");
    }
  }

  useEffect(() => {
    if (open) {
      handleDiscrepancyDetails()
      if (open.isScanning) {
        ref?.current?.focus()
      }
    } else {
      setDetails({});
      setAdditionalNotes({});
    }
  }, [open]);

  return (
    <Drawer
      open={Boolean(open)}
      TransitionComponent={TransitionComponent}
      keepMounted
      maxWidth='xs'
      fullWidth
      className="cus-drawer discrepancy-stock-drawer"
      onClose={handleClose}
      anchor='right'
    >
      {open?.isScanning && <BarcodeScanArea handleScanProduct={handleScanProduct} />}

      <Box ref={ref} p={1.25} className='d-flex align-item-center jc-s-btwn mb-3'>
        <Typography variant='h4' fontWeight='700' color="#2E2E3A" style={{ wordBreak: "break-word" }}>Discrepancy Details - {details?.discrepancyType}</Typography>
        <IconButton onClick={handleClose} edge='end' size='small'>
          <CloseIcon fontSize='medium' />
        </IconButton>
      </Box>

      <Box p={{ xs: 1.25 }} style={{
        maxHeight: details?.status && details?.status !== "resolved" && details?.status !== "rejected" ? "calc(100svh - 160px)" : "calc(100svh - 90px)",
        overflow: "auto"
      }}
      >
        <Grid container spacing={3}>
          {/* Add comment */}
          {details?.status && details?.status !== "resolved" && details?.status !== "rejected" && <><Grid item xs={12} md={12}>
            <Typography variant='h6' fontWeight="700" color="#2E2E3A" mb={1}>Add Note</Typography>
            <Card sx={{ borderRadius: 2.5, boxShadow: 'none', border: "1px solid #9A9AAF", overflow: "hidden" }}>
              <textarea
                placeholder='Add Note...'
                style={{ border: "none", outline: "none", padding: "12px 12px 12px 20px", resize: "none" }}
                name="additionalNote"
                value={additionalNotes?.additionalNote}
                onChange={handleChange}
              />
              <Stack direction='row' gap={1} alignItems='center' justifyContent="space-between" style={{ padding: "0 12px 12px 20px" }} className="upload-photo-drawer">
                <UploadPhoto
                  photos={additionalNotes?.additionalPhotos || []}
                  handleDeletePhoto={handleDeletePhoto}
                  updatePhotos={photo => setAdditionalNotes(prev => ({ ...prev, additionalPhotos: [...(prev?.additionalPhotos || []), photo] }))}
                  showLabel={false}
                />
                {/* <MDButton
                  variant="contained"
                  style={{ padding: "0", minWidth: "auto" }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M21 2.25H3C1.48438 2.25 0.25 3.4834 0.25 5V19C0.25 20.5166 1.48438 21.75 3 21.75H21C22.5156 21.75 23.75 20.5166 23.75 19V5C23.75 3.4834 22.5156 2.25 21 2.25ZM3 3.75H21C21.6895 3.75 22.25 4.31055 22.25 5V12.1885L17.5312 7.46973C17.2383 7.17676 16.7617 7.17676 16.4688 7.46973L8 15.9395L5.53125 13.4697C5.23828 13.1768 4.76172 13.1768 4.46875 13.4697L1.75 16.1885V5C1.75 4.31055 2.31055 3.75 3 3.75ZM21 20.25H3C2.31055 20.25 1.75 19.6895 1.75 19V18.3114L5 15.0605L7.46875 17.5303C7.76172 17.8232 8.23828 17.8232 8.53125 17.5303L17 9.06055L22.25 14.3115V19C22.25 19.6895 21.6895 20.25 21 20.25Z" fill="black" />
                    <path d="M7 11.75C8.51562 11.75 9.75 10.5166 9.75 9C9.75 7.4834 8.51562 6.25 7 6.25C5.48438 6.25 4.25 7.4834 4.25 9C4.25 10.5166 5.48438 11.75 7 11.75ZM7 7.75C7.68945 7.75 8.25 8.31055 8.25 9C8.25 9.68945 7.68945 10.25 7 10.25C6.31055 10.25 5.75 9.68945 5.75 9C5.75 8.31055 6.31055 7.75 7 7.75Z" fill="black" />
                  </svg>
                </MDButton> */}
                {/* <MDButton
                  variant="contained"
                  sx={{ minWidth: '90px', background: "#2E2E3A !important", color: "#ffffff !important", fontSize: "14px", minHeight: "36px", padding: "7px 20px" }}
                >
                  Add
                </MDButton> */}
              </Stack>
            </Card>
          </Grid>
            <Grid item xs={12}>
              <hr className='my-1' style={{ borderTop: "1px solid #D0D0DA", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
            </Grid>
          </>
          }

          {/* Basic Information */}
          <Grid item xs={12} md={12}>
            <Typography variant='h6' fontWeight="700" color="#2E2E3A" mb={2}>Basic Information</Typography>
            <ul className="w-100 d-grid grid-columns-two pb-2" style={{ gap: "12px" }}>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Driver</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.driver}</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Reported By</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.reportedBy}</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Distribution ID</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>D-1024</Typography>
              </li>
              {/* <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Discrepancy</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1">
                  <a target='_blank' style={{ fontWeight: "700", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                      <g clipPath="url(#clip0_17646_26061)">
                        <path d="M8.66665 12.5H1.99999C1.46573 12.5 0.963502 12.2919 0.585767 11.9142C0.208033 11.5364 0 11.0342 0 10.5V3.83333C0 3.2991 0.208057 2.79687 0.585791 2.41913C0.963525 2.0414 1.46576 1.83334 2.00001 1.83334H4.66667C5.03488 1.83334 5.33334 2.1318 5.33334 2.50001C5.33334 2.86822 5.03488 3.16668 4.66667 3.16668H1.99999C1.8219 3.16668 1.65451 3.23603 1.52861 3.36193C1.40272 3.48783 1.33334 3.65524 1.33334 3.83333V10.5C1.33334 10.6781 1.40269 10.8455 1.52861 10.9714C1.65451 11.0973 1.8219 11.1667 1.99999 11.1667H8.66662C8.84471 11.1667 9.0121 11.0973 9.13802 10.9714C9.26392 10.8455 9.33327 10.6781 9.33327 10.5V7.83333C9.33327 7.46512 9.63175 7.16668 9.99996 7.16668C10.3682 7.16668 10.6667 7.46514 10.6667 7.83333V10.5C10.6667 11.0342 10.4586 11.5364 10.0808 11.9142C9.70309 12.2919 9.20083 12.5 8.66665 12.5ZM4.66667 8.49998C4.49604 8.49998 4.32543 8.43491 4.19527 8.30473C3.93493 8.04439 3.93493 7.62225 4.19527 7.36191L9.72381 1.83334H7.33333C6.96512 1.83334 6.66668 1.53488 6.66668 1.16667C6.66668 0.798461 6.96512 0.5 7.33333 0.5H11.3333C11.4255 0.5 11.5133 0.518724 11.5932 0.552561C11.6678 0.584094 11.7378 0.629785 11.799 0.689659L11.7991 0.689705C11.7995 0.690148 11.8 0.690567 11.8004 0.69101C11.8005 0.691126 11.8007 0.691266 11.8008 0.691382C11.8011 0.691708 11.8015 0.692058 11.8018 0.692407C11.802 0.69264 11.8022 0.692849 11.8025 0.693082C11.8027 0.693338 11.803 0.693618 11.8032 0.693827C11.8037 0.694293 11.8042 0.694782 11.8047 0.695271C11.8051 0.695737 11.8056 0.696249 11.8061 0.696715C11.8064 0.696948 11.8066 0.697251 11.8068 0.69746C11.8071 0.697693 11.8073 0.697903 11.8075 0.698136C11.8079 0.698485 11.8082 0.698811 11.8085 0.699184C11.8086 0.699277 11.8088 0.69944 11.8089 0.699556C11.8093 0.699999 11.8098 0.700441 11.8102 0.700884L11.8102 0.70093C11.8701 0.762225 11.9158 0.832229 11.9473 0.906751C11.9812 0.98663 11.9999 1.07443 11.9999 1.16665V5.16665C11.9999 5.53486 11.7014 5.83332 11.3332 5.83332C10.965 5.83332 10.6666 5.53486 10.6666 5.16665V2.77616L5.13795 8.30475C5.00789 8.43493 4.83728 8.49998 4.66667 8.49998Z" fill="#2E2E3A" />
                      </g>
                      <defs>
                        <clipPath id="clip0_17646_26061">
                          <rect width="12" height="12" fill="white" transform="translate(0 0.5)" />
                        </clipPath>
                      </defs>
                    </svg>
                    <u>Link</u>
                  </a>
                </Typography>
              </li> */}
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Discrepancy Date</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{formatTimestampToWithoutTime(details?.discrepancyDate)}</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Discrepancy Type</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>
                  <Box display="flex" alignItems="center" gap={0.5} className="text-discrepancy">
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                      <path d="M7.88912 3.12793C9.04623 2.89781 10.2454 3.0163 11.3354 3.46777C12.4255 3.91928 13.3576 4.68403 14.0131 5.66504C14.6684 6.64587 15.018 7.79896 15.018 8.97852C15.0162 10.5601 14.3874 12.077 13.269 13.1953C12.1507 14.3136 10.6337 14.9425 9.0522 14.9443C7.87252 14.9442 6.71961 14.5939 5.73873 13.9385C4.75774 13.283 3.99298 12.3517 3.54146 11.2617C3.09001 10.1718 2.97157 8.97247 3.20162 7.81543C3.43179 6.65829 4.00021 5.59501 4.83443 4.76074C5.66871 3.92646 6.73193 3.35811 7.88912 3.12793ZM9.0522 11.1484C8.89156 11.1485 8.73463 11.1959 8.60103 11.2852C8.46726 11.3745 8.3628 11.5017 8.30123 11.6504C8.2397 11.799 8.22395 11.9624 8.25533 12.1201C8.28671 12.2779 8.36327 12.4233 8.47701 12.5371C8.59078 12.6509 8.7362 12.7284 8.894 12.7598C9.05171 12.7911 9.21516 12.7744 9.36373 12.7129C9.51237 12.6513 9.63958 12.5469 9.72896 12.4131C9.81818 12.2795 9.8656 12.1226 9.86568 11.9619C9.86568 11.7464 9.78065 11.5392 9.62838 11.3867C9.47582 11.2342 9.26795 11.1484 9.0522 11.1484ZM9.0522 5.18262C8.90855 5.18275 8.77098 5.24021 8.66939 5.3418C8.56782 5.44342 8.51029 5.58093 8.51021 5.72461V9.52148C8.51031 9.66515 8.56781 9.80269 8.66939 9.9043C8.77098 10.0059 8.90855 10.0633 9.0522 10.0635C9.19604 10.0635 9.33429 10.006 9.43599 9.9043C9.53757 9.80269 9.59507 9.66515 9.59517 9.52148V5.72461C9.59509 5.58093 9.53757 5.44342 9.43599 5.3418C9.33429 5.24009 9.19604 5.18262 9.0522 5.18262ZM9.40181 1.9375C11.1443 2.02383 12.798 2.75377 14.0376 3.99316C15.1511 5.10676 15.8514 6.55532 16.0454 8.10156C15.8501 6.55608 15.1487 5.10926 14.0356 3.99609C12.7964 2.7569 11.1438 2.02556 9.40181 1.9375Z" fill="#FD4438" />
                    </svg>
                    {/* <Typography color="#FD4438" fontWeight="600" style={{ fontSize: "12px" }}>{data?.discrepancyType}</Typography> */}
                    <span>{details?.discrepancyType}</span>
                  </Box>
                </Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Region</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.region?.region}</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Sub Region</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.subRegion?.name}</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Status</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>
                  <StatusText status={details?.status} />
                </Typography>
              </li>
            </ul>
          </Grid>

          <Grid item xs={12}>
            <hr className='my-1' style={{ borderTop: "1px solid #D0D0DA", borderBottom: "none", borderLeft: "none", borderRight: "none" }} />
          </Grid>

          {/* Product Details */}
          {details?.product && <Grid item xs={12} md={12}>
            <Typography variant='h6' fontWeight="700" color="#2E2E3A" mb={2}>Product Details</Typography>
            {(details?.product?.assets?.thumbURL || details?.product?.assets?.origURL) && <Box className="flex-1">
              <img src={details?.product?.assets?.thumbURL || details?.product?.assets?.origURL} style={{ width: "100px", height: "100px", objectFit: "cover", background: "#F1F1F1", borderRadius: "4px", overflow: "hidden", border: "none" }} />
            </Box>}
            <ul className="w-100 d-grid mt-2" style={{ gap: "12px", paddingBottom: "12px" }}>
              <li className="w-100 d-flex gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" style={{ minWidth: "183px" }}>Product Name</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.product?.name}</Typography>
              </li>
            </ul>
            <ul className="w-100 d-grid grid-columns-two pb-2" style={{ gap: "12px" }}>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Brand</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.product?.brand?.name}</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Scanned Qty</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.scannedQty || 0}/{details?.expectedQty || 0}</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">From Box</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.product?.box?.name}</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Sub Category</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.product?.category?.name}</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Weight</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>{details?.product?.weight || 0}g</Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Batch</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1">
                  {details?.product?.batches?.map((batch, idx) => (
                    <span key={batch?.batchNo + idx} style={{ wordBreak: "break-word" }}>
                      {batch?.sku} (Qty {batch?.qty}){idx < details?.product?.batches?.length - 1 ? ',' : null} <br />
                    </span>
                  ))}
                </Typography>
              </li>
              <li className="w-100 d-flex align-item-center gap-1">
                <Typography variant='body2' fontWeight="normal" color="#656575" className="flex-1">Value</Typography>
                <Typography variant='body2' fontWeight="500" color="#2E2E3A" className="flex-1" style={{ wordBreak: "break-word" }}>${details?.product?.totalAmount?.toLocaleString() || 0}</Typography>
              </li>
            </ul>
          </Grid>}

          {/* Notes */}
          {(details?.note || details?.photos?.length > 0) && <Grid item xs={12} md={12}>
            <Card sx={{ p: 2, borderRadius: 2.5, boxShadow: 'none', background: '#F2F3F7' }}>
              <Typography variant='h6' fontWeight="700" color="#2E2E3A" mb={1}>Notes</Typography>
              <Typography variant='body2' fontWeight="normal" color="#656575" style={{ wordBreak: "break-word" }}>{details?.note}</Typography>
              <Box display="flex" alignItems="start" flexWrap="wrap" gap={1} style={{ padding: "12px 0 16px" }}>
                {
                  details?.photos?.map(photo => (
                    <Box key={photo} style={{ width: "80px", height: "80px", borderRadius: "12px", background: "#F1F1F1", overflow: "hidden" }}>
                      <img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </Box>
                  ))
                }
              </Box>
              {/* <Box display="flex" alignItems="center" gap={1}>
                <Typography variant='body2' fontWeight="600" lineHeight="normal" color="#2E2E3A">Sarah Chen</Typography>
                <span style={{ lineHeight: "normal" }}>|</span>
                <Typography variant='body2' fontWeight="500" lineHeight="normal" color="#2E2E3A">June 24, 2025</Typography>
              </Box> */}
            </Card>
          </Grid>}

          {/* Driver comments */}
          {(details?.driverComments?.comment || details?.driverComments?.photo?.length > 0) && <Grid item xs={12} md={12}>
            <Card sx={{ p: 2, borderRadius: 2.5, boxShadow: 'none', background: '#F2F3F7' }}>
              <Typography variant='h6' fontWeight="700" color="#2E2E3A" mb={1}>Driver Comments</Typography>
              <Typography variant='body2' fontWeight="normal" color="#656575" style={{ wordBreak: "break-word" }}>{details?.driverComments?.comment}</Typography>
              <Box display="flex" alignItems="start" flexWrap="wrap" gap={1} style={{ padding: "12px 0 16px" }}>
                {
                  details?.driverComments?.photos?.map(photo => (
                    <Box key={photo} style={{ width: "80px", height: "80px", borderRadius: "12px", background: "#F1F1F1", overflow: "hidden" }}>
                      <img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </Box>
                  ))
                }
              </Box>
              {/* <Box display="flex" alignItems="center" gap={1}>
                <Typography variant='body2' fontWeight="600" lineHeight="normal" color="#2E2E3A">Sarah Chen</Typography>
                <span style={{ lineHeight: "normal" }}>|</span>
                <Typography variant='body2' fontWeight="500" lineHeight="normal" color="#2E2E3A">June 24, 2025</Typography>
              </Box> */}
            </Card>
          </Grid>}

          {/* Addition Notes */}
          {(details?.additionalNote || details?.additionalPhotos?.length > 0) && <Grid item xs={12} md={12}>
            <Card sx={{ p: 2, borderRadius: 2.5, boxShadow: 'none', background: '#F2F3F7' }}>
              <Typography variant='h6' fontWeight="700" color="#2E2E3A" mb={1}>Additional Notes</Typography>
              <Typography variant='body2' fontWeight="normal" color="#656575" style={{ wordBreak: "break-word" }}>{details?.additionalNote}</Typography>
              <Box display="flex" alignItems="start" flexWrap="wrap" gap={1} style={{ padding: "12px 0 16px" }}>
                {
                  details?.additionalPhotos?.map(photo => (
                    <Box key={photo} style={{ width: "80px", height: "80px", borderRadius: "12px", background: "#F1F1F1", overflow: "hidden" }}>
                      <img src={photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </Box>
                  ))
                }
              </Box>
            </Card>
          </Grid>}

        </Grid>
      </Box>

      {/* Action buttons */}
      {details?.status && details?.status !== "resolved" && details?.status !== "rejected" &&
        <Stack direction="row" alignItems="center" p={2.25} gap={1} style={{ width: "100%", margin: "0 -10px -10px -10px", minHeight: '80px', background: "#ffffff", boxShadow: "0 -5px 4px 0 rgba(213, 220, 227, 0.25)" }}>
          {(!details?.product || details?.scannedQty >= details?.expectedQty) && <MDButton
            style={{ border: '1px solid #24CA49', backgroundColor: '#24CA49', color: '#ffffff' }}
            disabled={isLoading}
            onClick={() =>
              dispatch(openCustomModalDialog({
                modalData: {
                  handleSubmit: () => handleUpdateDiscrepancy("approved"),
                  title: "Approve Discrepancy",
                  description: <>
                    This will approve the discrepancy.
                    <br />
                    Are you sure you want to approve?
                  </>,
                  submitBtnColor: "success",
                  submitBtnLabel: "Approve",
                  actionType: "approve"
                },
                modal: 'CONFIRMATION_MODAL'
              }))}
          >
            {isLoading ? <CircularProgress size={22} color="light" /> : "Verify and Approve"}
          </MDButton>}
          <MDButton
            style={{ border: '1px solid #FD4438', backgroundColor: '#FD4438', color: '#ffffff' }}
            disabled={isLoading}
            onClick={() => {
              dispatch(openCustomModalDialog({
                modalData: {
                  handleSubmit: () => handleUpdateDiscrepancy("rejected"),
                  title: "Reject Discrepancy",
                  description: <>
                    This will reject the discrepancy.
                    <br />
                    Are you sure you want to reject?
                  </>,
                  submitBtnLabel: "Reject",
                  actionType: "reject"
                },
                modal: 'CONFIRMATION_MODAL'
              }))
            }}
          >
            {isLoading ? <CircularProgress size={22} color="light" /> : "Reject"}
          </MDButton>
        </Stack>
      }

    </Drawer>
  )
}
